import SortableTableHead, { HeadCell } from "components/Common/Table/SortableTableHead.tsx";
import { Tab, TableBody, TableRow, Tabs } from "@mui/material";
import { StyledTableCell, StyledTableWrapper } from "components/Common/Table/StyledTable.styles.ts";
import { useHandleTableSort } from "hooks/useHandleTableSort.ts";
import { Fragment, SyntheticEvent, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { LinkButton } from "components/Common/Link";
import {
	ContactCareForSearchHelpText,
	CustomItemNameText,
	CustomQuoteNumberText,
	CustomQuoteRequestACheckYourSpellingBullet,
	CustomQuoteRequestAdjustYourFiltersBullet,
	CustomQuoteRequestSubmittingBullet,
	DesignerNumberText,
	ExpiredQuotesInfoText,
	ExpiredQuotesText,
	LastUpdatedText,
	OpenRequestsText,
	OpenText,
	ProductLineText,
	QuoteSearchBarText,
	QuoteTableNoResultsFoundHeader,
	QuoteTableNoResultsFoundSubheader,
	RemoveText,
	StatusText,
	UnavailableDataPlaceholderText,
	UpdateAvailableText,
	UpdateText
} from "constants/text.ts";
import {
	CustomQuoteExteriorTableHeader,
	ExpiredQuotesInfo,
	ViewQuoteTableContainer,
	ViewQuoteTableFilters
} from "./ViewQuoteTable.styles.ts";
import useSession from "hooks/useSession.ts";
import { CacheKeys, InitialQuoteFilterOptions } from "pages/OrderPages/OrderListPage/orderListConstants.ts";
import { displayDesigner } from "utils/order.ts";
import CustomQuoteTableFilters from "../CustomQuoteTableFilters.tsx";
import SuggestedSearch from "components/Common/SuggestedSearch/SuggestedSearch.tsx";
import { models } from "types/api/viewModels.ts";
import { formatQuoteStatus, getFilteredQuotes, getRequestNames, getRevisionIdNumbers } from "../customQuoteUtils.ts";
import { CustomQuoteStatus } from "types/declarations/enums.ts";
import {
	CustomQuoteRequestAssignedToChip,
	CustomQuoteRequestNoResultsFoundContainer
} from "../CustomQuoteRequestTable.styles.ts";
import { useDispatch } from "react-redux";
import { newOrderActions } from "features/reducers/newOrder/newOrder.ts";
import { QuoteFilterOpts } from "pages/CustomQuote/CustomQuoteLandingPage/CustomQuoteLandingPage.tsx";
import { useDialog } from "hooks/useDialog.ts";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";
import TableSkeletonWithTabs from "../../Common/Table/TableSkeleton/TableSkeletonWithTabs.tsx";
import { useNavigate } from "react-router";

interface ViewQuotesTableProps {
	customQuotes: models["CustomQuoteViewModel"][];
	isLoading?: boolean;
	userDetails: models["UserViewModel"] | undefined;
}

const ViewQuoteTable = ({ customQuotes, isLoading, userDetails }: ViewQuotesTableProps) => {
	const baseViewQuoteRequestTableHeadCells = useMemo(
		() =>
			[
				{ id: "updatedAt", label: LastUpdatedText, sortable: true },
				{ id: "customQuoteNumber", label: CustomQuoteNumberText },
				{ id: "customItemName", label: CustomItemNameText },
				{ id: "designerNumber", label: DesignerNumberText },
				{ id: "productLine", label: ProductLineText },
				{ id: "status", label: StatusText },
				{ id: "update", label: UpdateText },
				{ id: "open", label: "" }
			].map((cell) => ({
				...cell,
				disablePadding: false,
				numeric: false,
				sortable: cell.sortable ?? false
			})) satisfies HeadCell[],
		[]
	);

	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(newOrderActions.clearNewOrder());
	}, [dispatch]);

	const [quoteFilterOptions, setQuoteFilterOptions] = useSession<QuoteFilterOpts>(
		CacheKeys.QuoteFilters,
		InitialQuoteFilterOptions
	);

	const { openDialog } = useDialog();

	useEffect(() => {
		dispatch(newOrderActions.clearNewOrder());
	}, [dispatch]);

	const [searchValue, setSearchValue] = useState<string | null>(null);

	const filteredQuotes = useMemo(() => {
		return getFilteredQuotes(quoteFilterOptions, customQuotes ?? []);
	}, [customQuotes, quoteFilterOptions]);

	const filteredOpenQuotes = filteredQuotes.filter(
		(quote: models["CustomQuoteViewModel"]) => quote.status !== CustomQuoteStatus.Expired
	);
	const filteredExpiredQuotes = filteredQuotes.filter(
		(quote: models["CustomQuoteViewModel"]) => quote.status === CustomQuoteStatus.Expired
	);

	const [tabValue, setTabValue] = useState(0);
	const handleTabChange = (event: SyntheticEvent, newValue: number) => {
		setTabValue(newValue);
		setSearchValue(null);
	};

	const viewQuoteRequestTableHeadCells = useMemo(() => {
		if (tabValue === 1) {
			return baseViewQuoteRequestTableHeadCells.filter((cell) => cell.id !== "update" && cell.id !== "status");
		}
		return baseViewQuoteRequestTableHeadCells;
	}, [baseViewQuoteRequestTableHeadCells, tabValue]);

	const applySearchFilter = (quotes: models["CustomQuoteViewModel"][]) => {
		if (!searchValue) return quotes;
		const searchTerm = searchValue.toUpperCase().trim();
		return quotes.filter((quote) => {
			const quoteName = quote.requestName?.toUpperCase() || "";
			const quoteRevisionId = quote.revisions?.flatMap((revision) => revision.revisionId).toString();
			return quoteName.includes(searchTerm) || quoteRevisionId?.includes(searchTerm);
		});
	};

	const openQuotes = applySearchFilter(filteredOpenQuotes);
	const expiredQuotes = applySearchFilter(filteredExpiredQuotes);

	const getQuoteSearchOptions = (quotes: models["CustomQuoteViewModel"][]): string[] => {
		const requestName = getRequestNames(quotes) ?? [];
		const revisionIds = getRevisionIdNumbers(quotes) ?? [];
		return [...requestName, ...revisionIds];
	};

	const handleQuoteSearch = (_event: SyntheticEvent, values: string | null) => {
		setSearchValue(values ?? null);
	};

	const { getComparator, handleRequestSort, order, orderBy } = useHandleTableSort("updatedAt", "desc");
	const sortedOpenQuotes = openQuotes.slice().sort(getComparator(order, orderBy));
	const sortedExpiredQuotes = expiredQuotes.slice().sort(getComparator(order, orderBy));
	const navigate = useNavigate();

	const displayedQuotes = tabValue === 0 ? sortedOpenQuotes : sortedExpiredQuotes;
	const currentTabQuotes = tabValue === 0 ? openQuotes : expiredQuotes;

	const searchOptions = useMemo(() => getQuoteSearchOptions(currentTabQuotes), [currentTabQuotes]);

	const GetViewQuoteTableRows = (
		request: models["CustomQuoteViewModel"],
		openDialog: ReturnType<typeof useDialog>["openDialog"]
	) => {
		const quoteTableCells = [
			{
				testId: "quotes-table-lastUpdated",
				props: { fixedWidth: "140px" },
				content: request.updatedAt
					? format(request.updatedAt, "MM/dd/yy hh:mm:ss")
					: UnavailableDataPlaceholderText
			},
			{
				testId: "quotes-table-customQuoteNumber",
				props: { fixedWidth: "80px" },
				content: request?.revisions?.length
					? [...request.revisions]
							.sort((a, b) => new Date(b.timestamp ?? 0).getTime() - new Date(a.timestamp ?? 0).getTime())
							.map((revision) => <div key={revision.revisionId}>{revision.revisionId}</div>)
					: UnavailableDataPlaceholderText
			},
			{
				testId: "quotes-table-customItemName",
				props: { minWidth: "125px", maxWidth: "170.5px" },
				content: request?.requestName ?? UnavailableDataPlaceholderText
			},
			{
				testId: "quotes-table-designerNumber",
				props: { minWidth: "100px", maxWidth: "170.5px" },
				content: displayDesigner(request.designer ?? request.designerId)
			},
			{
				testId: "quotes-table-productLine",
				props: { minWidth: "100px", maxWidth: "110px" },
				content: request?.globals?.productLine?.description ?? UnavailableDataPlaceholderText
			}
		];

		return (
			<Fragment>
				{quoteTableCells.map(({ testId, props, content }) => (
					<StyledTableCell
						key={testId}
						data-testid={testId}
						{...props}
					>
						{content}
					</StyledTableCell>
				))}
				{tabValue === 0 && (
					<>
						<StyledTableCell
							data-testid="request-table-update"
							fixedWidth="105px"
						>
							{formatQuoteStatus(request?.status)}
						</StyledTableCell>
						<StyledTableCell
							data-testid="request-table-update"
							fixedWidth="105px"
						>
							{userDetails?.role === request.responsibleParty && (
								<CustomQuoteRequestAssignedToChip
									data-testid="request-table-update-chip"
									color="default"
									variant="filled"
									label={UpdateAvailableText}
								/>
							)}
						</StyledTableCell>
					</>
				)}
				<StyledTableCell
					fixedWidth="60px"
					isLastIndex
				>
					<LinkButton
						onClick={() => navigate(`/custom-quote/${request.quoteId}`)}
						data-testid="quotes-table-open-link"
					>
						{OpenText}
					</LinkButton>
					<LinkButton
						variant="remove"
						onClick={() =>
							openDialog({ dialogStatus: DialogTracking.REMOVE_CUSTOM_QUOTE, dialogId: request.quoteId })
						}
						data-testid="quotes-table-remove-link"
					>
						{RemoveText}
					</LinkButton>
				</StyledTableCell>
			</Fragment>
		);
	};

	return (
		<ViewQuoteTableContainer
			elevation={1}
			square
		>
			{isLoading ? (
				<TableSkeletonWithTabs
					tabLabels={[OpenRequestsText, ExpiredQuotesText]}
					disclaimerText={ExpiredQuotesInfoText}
				/>
			) : (
				<>
					<CustomQuoteExteriorTableHeader>
						<div>
							<Tabs
								value={tabValue}
								onChange={handleTabChange}
								textColor="primary"
								aria-label="Custom quote table tabs"
							>
								<Tab
									label={OpenRequestsText}
									data-testid="open-requests-tab"
								/>
								<Tab
									label={ExpiredQuotesText}
									data-testid="expired-quotes-tab"
								/>
							</Tabs>
						</div>

						<ViewQuoteTableFilters>
							<CustomQuoteTableFilters
								filterOptions={quoteFilterOptions}
								setFilterOptions={setQuoteFilterOptions}
								quotes={
									tabValue === 0
										? customQuotes.filter((q) => q.status !== CustomQuoteStatus.Expired)
										: customQuotes.filter((q) => q.status === CustomQuoteStatus.Expired)
								}
								tabValue={tabValue}
							/>

							<SuggestedSearch
								placeholder={QuoteSearchBarText}
								handleSearch={handleQuoteSearch}
								searchOptions={searchOptions}
								searchValue={searchValue ?? ""}
								data-testid="custom-quote-search"
								ariaLabel="Search custom quotes"
								dataTestId="custom-quote-search"
								dropDownOpenOnChar={2}
								id="custom-quote-search"
								size="small"
							/>
						</ViewQuoteTableFilters>
					</CustomQuoteExteriorTableHeader>

					<ExpiredQuotesInfo>{ExpiredQuotesInfoText}</ExpiredQuotesInfo>

					{displayedQuotes.length > 0 ? (
						<StyledTableWrapper
							data-testid="quotes-table"
							aria-label="Quotes Table"
						>
							<SortableTableHead
								orderBy={orderBy}
								order={order}
								onRequestSort={handleRequestSort}
								headCells={viewQuoteRequestTableHeadCells}
							/>
							<TableBody>
								{displayedQuotes.map((quote) => (
									<TableRow
										key={quote.quoteId}
										data-testid={`quote-${quote.quoteId}`}
									>
										{GetViewQuoteTableRows(quote, openDialog)}
									</TableRow>
								))}
							</TableBody>
						</StyledTableWrapper>
					) : (
						<CustomQuoteRequestNoResultsFoundContainer>
							<img
								src="/assets/CustomQuoteNoResults.svg"
								alt="No Results Found"
								data-testid="custom-quote-no-results-found"
							/>
							<h1>{QuoteTableNoResultsFoundHeader}</h1>
							<div>
								<div>{QuoteTableNoResultsFoundSubheader}</div>
								<ul>
									<li>{CustomQuoteRequestAdjustYourFiltersBullet}</li>
									<li>{CustomQuoteRequestACheckYourSpellingBullet}</li>
									<li>{CustomQuoteRequestSubmittingBullet}</li>
								</ul>
							</div>
							<div>{ContactCareForSearchHelpText}</div>
						</CustomQuoteRequestNoResultsFoundContainer>
					)}
				</>
			)}
		</ViewQuoteTableContainer>
	);
};

export default ViewQuoteTable;
